import { Button } from "@/components/ui/button";
import { Calendar, Menu, X } from "lucide-react";
import { Link, useLocation } from "wouter";
import { scrollToTop } from "@/hooks/use-scroll-to-top";

import { useState } from "react";
import logoImage from "@assets/image_1749670714467.png";

interface NavigationProps {
  onBookCall?: () => void;
}

export default function Navigation({ onBookCall }: NavigationProps) {
  const [location] = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  return (
    <header className="fixed top-0 w-full z-50 glassmorphism">
      <div className="container mx-auto px-4 md:px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          <Link href="/" onClick={scrollToTop}>
            <div className="flex items-center space-x-2 cursor-pointer flex-shrink-0">
              <img 
                src={logoImage} 
                alt="UnicornClub Logo" 
                className="w-10 h-10 md:w-12 md:h-12 object-contain"
              />
              <span className="text-lg md:text-2xl font-bold text-white whitespace-nowrap">UnicornClub</span>
            </div>
          </Link>
          <div className="hidden md:flex items-center space-x-6">
            <Link 
              href="/howitworks" 
              onClick={scrollToTop}
              className={`text-gray-300 hover:text-white transition-colors font-medium ${
                location === "/howitworks" ? "text-white font-semibold" : ""
              }`}
            >
              How It Works
            </Link>
            <Link 
              href="/about" 
              onClick={scrollToTop}
              className={`text-gray-300 hover:text-white transition-colors font-medium ${
                location === "/about" ? "text-white font-semibold" : ""
              }`}
            >
              About Us
            </Link>
            <Link 
              href="/faq" 
              onClick={scrollToTop}
              className={`text-gray-300 hover:text-white transition-colors font-medium ${
                location === "/faq" ? "text-white font-semibold" : ""
              }`}
            >
              FAQ
            </Link>
            <Link 
              href="/blog" 
              onClick={scrollToTop}
              className={`text-gray-300 hover:text-white transition-colors font-medium ${
                location === "/blog" || location.startsWith("/blog/") ? "text-white font-semibold" : ""
              }`}
            >
              Blog
            </Link>

            {onBookCall && (
              <Link href="/project-form" onClick={scrollToTop}>
                <Button 
                  className="bg-cyber-gradient hover:bg-gradient-to-r hover:from-neon-pink hover:to-cyber-purple text-white font-semibold py-3 px-6 rounded-full transition-all duration-300 transform hover:scale-105 animate-glow"
                >
                  <Calendar className="mr-2 w-4 h-4" />
                  Book Free Discovery Call
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="md:hidden p-2 text-white hover:text-cyber-purple transition-colors"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-charcoal/95 backdrop-blur-md border-t border-gray-700">
            <div className="container mx-auto px-4 py-4 space-y-4">
              <Link 
                href="/howitworks" 
                onClick={() => { scrollToTop(); closeMenu(); }}
                className={`block text-gray-300 hover:text-white transition-colors font-medium py-2 ${
                  location === "/howitworks" ? "text-white font-semibold" : ""
                }`}
              >
                How It Works
              </Link>
              <Link 
                href="/about" 
                onClick={() => { scrollToTop(); closeMenu(); }}
                className={`block text-gray-300 hover:text-white transition-colors font-medium py-2 ${
                  location === "/about" ? "text-white font-semibold" : ""
                }`}
              >
                About Us
              </Link>
              <Link 
                href="/faq" 
                onClick={() => { scrollToTop(); closeMenu(); }}
                className={`block text-gray-300 hover:text-white transition-colors font-medium py-2 ${
                  location === "/faq" ? "text-white font-semibold" : ""
                }`}
              >
                FAQ
              </Link>
              <Link 
                href="/blog" 
                onClick={() => { scrollToTop(); closeMenu(); }}
                className={`block text-gray-300 hover:text-white transition-colors font-medium py-2 ${
                  location === "/blog" || location.startsWith("/blog/") ? "text-white font-semibold" : ""
                }`}
              >
                Blog
              </Link>
              {onBookCall && (
                <Link href="/project-form" onClick={() => { 
                  scrollToTop(); 
                  closeMenu(); 
                }}>
                  <Button 
                    className="w-full bg-cyber-gradient hover:bg-gradient-to-r hover:from-neon-pink hover:to-cyber-purple text-white font-semibold py-3 px-6 rounded-full transition-all duration-300 mt-2"
                  >
                    <Calendar className="mr-2 w-4 h-4" />
                    Book Free Discovery Call
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}